import React, { useEffect, useRef, useState } from 'react';

import { useAuth } from '../systems/auth-context';

import {

  canAccessAnalytics,

  canAccessMasterDashboard,

  canAccessScenarioTools,

  canAccessReviewsDashboard,

} from '../systems/roles';

import { useWorkspace } from '../systems/workspace-context';

import { useLeaderboardEnabled } from '../systems/use-leaderboard-enabled';

import styles from './navigation.module.css';



interface NavigationProps {

  currentScreen: string;

  onScreenChange: (screen: any) => void;

  onOpenProfile: () => void;

}



export const Navigation: React.FC<NavigationProps> = ({ 

  currentScreen, 

  onScreenChange, 

  onOpenProfile 

}) => {

  const { user, logout } = useAuth();
  const { workspaces, workspaceId, setWorkspaceId } = useWorkspace();
  const leaderboardEnabled = useLeaderboardEnabled();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [isGlobalAdminMenuOpen, setIsGlobalAdminMenuOpen] = useState(false);

  const [isEditorMenuOpen, setIsEditorMenuOpen] = useState(false);

  const userMenuRef = useRef<HTMLDivElement>(null);

  const globalAdminMenuRef = useRef<HTMLDivElement>(null);

  const editorMenuRef = useRef<HTMLDivElement>(null);

  const isAnyMenuOpen = isMenuOpen || isGlobalAdminMenuOpen || isEditorMenuOpen;

  useEffect(() => {
    if (!isAnyMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        userMenuRef.current?.contains(target) ||
        globalAdminMenuRef.current?.contains(target) ||
        editorMenuRef.current?.contains(target)
      ) {
        return;
      }

      setIsMenuOpen(false);
      setIsGlobalAdminMenuOpen(false);
      setIsEditorMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAnyMenuOpen]);

  if (!user) return null;



  const isGlobalAdmin = canAccessMasterDashboard(user);

  const isDxEditorOnly = canAccessScenarioTools(user) && !isGlobalAdmin;

  const showAnalyticsAdminLink = canAccessAnalytics(user) && !isGlobalAdmin && !canAccessScenarioTools(user);

  const isEnterpriseDashboardScreen = ['dashboard', 'enterpriseDashboard', 'analytics'].includes(currentScreen);



  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const toggleGlobalAdminMenu = () => setIsGlobalAdminMenuOpen(!isGlobalAdminMenuOpen);

  const toggleEditorMenu = () => setIsEditorMenuOpen(!isEditorMenuOpen);

  const handleAction = (action: () => void) => {

    action();

    setIsMenuOpen(false);

    setIsGlobalAdminMenuOpen(false);

    setIsEditorMenuOpen(false);

  };



  const renderEditorMenuItems = () => (

    <>

      <button 

        className={`${styles.menuItem} ${currentScreen === 'scenarioStudio' ? styles.activeItem : ''}`}

        onClick={() => handleAction(() => onScreenChange('scenarioStudio'))}

      >

        🎬 Scenario Studio

      </button>

      <button 

        className={`${styles.menuItem} ${currentScreen === 'admin' ? styles.activeItem : ''}`}

        onClick={() => handleAction(() => onScreenChange('admin'))}

      >

        ✍️ Story Admin

      </button>

      {canAccessReviewsDashboard(user) && (

        <button 

          className={`${styles.menuItem} ${currentScreen === 'reviews' ? styles.activeItem : ''}`}

          onClick={() => handleAction(() => onScreenChange('reviews'))}

        >

          💬 Reviews

        </button>

      )}

    </>

  );



  return (

    <nav className={styles.nav}>

      <div className={styles.logoWrapper}>

        <div className={styles.logo} onClick={() => onScreenChange('selection')}>

          Cyber<span>Novel</span>

          <div className={styles.logoDot} />

        </div>

        <a 

          href="https://darwin-x.com" 

          target="_blank" 

          rel="noopener noreferrer" 

          className={styles.brandLink}

        >

          By Darwin X

        </a>

      </div>



      {isGlobalAdmin && workspaces.length > 0 && (
        <div className={styles.workspaceSelectWrapper}>
          <label className={styles.workspaceLabel} htmlFor="workspace-select">
            Workspace
          </label>
          <select
            id="workspace-select"
            className={styles.workspaceSelect}
            value={workspaceId ?? ''}
            onChange={(e) => {
              setWorkspaceId(e.target.value);
              window.location.reload();
            }}
          >
            {workspaces.map((workspace) => (
              <option key={workspace.id} value={workspace.id}>
                {workspace.name}
              </option>
            ))}
          </select>
        </div>
      )}



      <div className={styles.navLinks}>

        <button 

          className={`${styles.navLink} ${currentScreen === 'selection' ? styles.active : ''}`}

          onClick={() => onScreenChange('selection')}

        >

          Histoires

        </button>

        {leaderboardEnabled && (
          <button
            className={`${styles.navLink} ${currentScreen === 'leaderboard' ? styles.active : ''}`}
            onClick={() => onScreenChange('leaderboard')}
          >
            Classement
          </button>
        )}



        {showAnalyticsAdminLink && (

          <button 

            className={`${styles.navLink} ${isEnterpriseDashboardScreen ? styles.active : ''}`}

            onClick={() => onScreenChange('dashboard')}

          >

            Admin

          </button>

        )}



        {isDxEditorOnly && (

          <>

            <button 

              className={`${styles.navLink} ${isEnterpriseDashboardScreen ? styles.active : ''}`}

              onClick={() => onScreenChange('dashboard')}

            >

              Admin

            </button>



            <div className={styles.adminMenuContainer} ref={editorMenuRef}>

              <button 

                className={`${styles.navLink} ${['scenarioStudio', 'admin', 'reviews'].includes(currentScreen) ? styles.active : ''}`}

                onClick={toggleEditorMenu}

              >

                Édition DX <span className={styles.chevronSmall}>{isEditorMenuOpen ? '▴' : '▾'}</span>

              </button>



              {isEditorMenuOpen && (

                <div className={styles.dropdownMenu}>

                  {renderEditorMenuItems()}

                </div>

              )}

            </div>

          </>

        )}



        {isGlobalAdmin && (

          <>

            <button 

              className={`${styles.navLink} ${isEnterpriseDashboardScreen ? styles.active : ''}`}

              onClick={() => onScreenChange('enterpriseDashboard')}

            >

              Admin

            </button>



            <div className={styles.adminMenuContainer} ref={globalAdminMenuRef}>

              <button 

                className={`${styles.navLink} ${currentScreen === 'dashboard' ? styles.active : ''}`}

                onClick={toggleGlobalAdminMenu}

              >

                Global Admin <span className={styles.chevronSmall}>{isGlobalAdminMenuOpen ? '▴' : '▾'}</span>

              </button>



              {isGlobalAdminMenuOpen && (

                <div className={`${styles.dropdownMenu} ${styles.globalAdminDropdown}`}>

                    <button 

                      className={`${styles.menuItem} ${currentScreen === 'dashboard' ? styles.activeItem : ''}`}

                      onClick={() => handleAction(() => onScreenChange('dashboard'))}

                    >

                      📊 Master Dashboard

                    </button>

                    {renderEditorMenuItems()}

                </div>

              )}

            </div>

          </>

        )}

      </div>



      <div className={styles.userMenuContainer} ref={userMenuRef}>

        <button className={styles.userToggle} onClick={toggleMenu}>

          <span className={styles.userAvatar}>👤</span>

          <span className={styles.userName}>{user.email.split('@')[0]}</span>

          <span className={styles.chevron}>{isMenuOpen ? '▴' : '▾'}</span>

        </button>



        {isMenuOpen && (

            <div className={styles.dropdownMenu}>

              <button 

                className={styles.menuItem}

                onClick={() => handleAction(onOpenProfile)}

              >

                ⚙️ Paramètres Profil

              </button>

              

              <div className={styles.divider} />

              

              <button 

                className={`${styles.menuItem} ${styles.logout}`}

                onClick={() => handleAction(logout)}

              >

                🚪 Déconnexion

              </button>

            </div>

        )}

      </div>

    </nav>

  );

};


